/**
 * Archive details — editable after creation.
 *
 * A records archive is long-lived: its description, source system, retention
 * schedule and storage target legitimately change over years. What makes that
 * defensible is not forbidding it but recording it — every save emits a
 * before/after audit event carrying the lifecycle state it was made in.
 *
 * `code` and `kind` are shown but not editable: the code is the stable handle
 * other records point at, and the kind decides which content tables hang off
 * the container. Changing either would orphan what is already archived.
 */

import {
  Alert,
  Autocomplete,
  Badge,
  Button,
  Group,
  Paper,
  Select,
  Stack,
  Switch,
  Text,
  TextInput,
  Textarea,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { IconInfoCircle } from "@tabler/icons-react";
import { useEffect, useState } from "react";

import { useAccessToken } from "@/features/auth/useAccessToken";
import {
  listArchiveClusters,
  updateArchiveContainer,
  type ArchiveContainer,
} from "@/shared/api/archive";
import { CLUSTER_LABEL } from "./format";

export function ArchiveDetailsTab({
  container,
  canEdit,
  onSaved,
}: {
  container: ArchiveContainer;
  canEdit: boolean;
  onSaved: () => void;
}) {
  const getToken = useAccessToken();
  const [busy, setBusy] = useState(false);
  const [clusterOptions, setClusterOptions] = useState<string[]>([]);

  useEffect(() => {
    void (async () => {
      try {
        setClusterOptions(await listArchiveClusters(await getToken()));
      } catch {
        setClusterOptions([]);
      }
    })();
  }, [getToken]);

  const [form, setForm] = useState({
    name: container.name,
    description: container.description ?? "",
    cluster: container.cluster,
    source_system: container.source_system,
    decommissioned_on: container.decommissioned_on ?? "",
    retention_class: container.retention_class,
    object_lock_mode: container.object_lock_mode ?? "",
    storage_class: container.storage_class,
    storage_prefix: container.storage_prefix,
    content_indexing_enabled: container.content_indexing_enabled,
  });

  useEffect(() => {
    setForm({
      name: container.name,
      description: container.description ?? "",
      cluster: container.cluster,
      source_system: container.source_system,
      decommissioned_on: container.decommissioned_on ?? "",
      retention_class: container.retention_class,
      object_lock_mode: container.object_lock_mode ?? "",
      storage_class: container.storage_class,
      storage_prefix: container.storage_prefix,
      content_indexing_enabled: container.content_indexing_enabled,
    });
  }, [container]);

  const dirty =
    form.name !== container.name ||
    form.description !== (container.description ?? "") ||
    form.cluster !== container.cluster ||
    form.source_system !== container.source_system ||
    form.decommissioned_on !== (container.decommissioned_on ?? "") ||
    form.retention_class !== container.retention_class ||
    form.object_lock_mode !== (container.object_lock_mode ?? "") ||
    form.storage_class !== container.storage_class ||
    form.storage_prefix !== container.storage_prefix ||
    form.content_indexing_enabled !== container.content_indexing_enabled;

  async function save() {
    setBusy(true);
    try {
      const token = await getToken();
      await updateArchiveContainer(token, container.container_id, {
        name: form.name,
        description: form.description,
        cluster: form.cluster,
        source_system: form.source_system,
        decommissioned_on: form.decommissioned_on || null,
        retention_class: form.retention_class,
        object_lock_mode: form.object_lock_mode || null,
        storage_class: form.storage_class,
        storage_prefix: form.storage_prefix,
        content_indexing_enabled: form.content_indexing_enabled,
      });
      notifications.show({
        title: "Archive updated",
        message: "The change is recorded in the audit trail with its before and after.",
        color: "teal",
      });
      onSaved();
    } catch (err) {
      notifications.show({
        title: "Could not save",
        message: err instanceof Error ? err.message : String(err),
        color: "red",
      });
    } finally {
      setBusy(false);
    }
  }

  return (
    <Stack gap="md" maw={720}>
      <Alert icon={<IconInfoCircle size={18} />} variant="light" color="navy">
        Every change here is written to the audit trail with its before and after, and with the
        lifecycle state it was made in. Retention changes apply to the <strong>next</strong> intake
        — records already archived keep the term they were archived under.
      </Alert>

      <Paper withBorder p="md" radius="md">
        <Group gap="xs" mb="md">
          <Badge variant="default" tt="none">
            Code {container.code}
          </Badge>
          <Badge variant="default" tt="none">
            {container.kind === "datasets" ? "Retired-system tables" : "Documents"}
          </Badge>
          <Text size="xs" c="dimmed">
            Fixed — other records point at these.
          </Text>
        </Group>

        <Stack gap="sm">
          <TextInput
            label="Name"
            value={form.name}
            disabled={!canEdit}
            onChange={(e) => setForm((v) => ({ ...v, name: e.currentTarget.value }))}
          />
          <Textarea
            label="Description"
            autosize
            minRows={2}
            value={form.description}
            disabled={!canEdit}
            onChange={(e) => setForm((v) => ({ ...v, description: e.currentTarget.value }))}
          />
          <Group grow>
            <TextInput
              label="Source system"
              value={form.source_system}
              disabled={!canEdit}
              onChange={(e) => setForm((v) => ({ ...v, source_system: e.currentTarget.value }))}
            />
            <TextInput
              label="Decommissioned on"
              placeholder="YYYY-MM-DD"
              value={form.decommissioned_on}
              disabled={!canEdit}
              onChange={(e) => setForm((v) => ({ ...v, decommissioned_on: e.currentTarget.value }))}
            />
          </Group>
          {/* Same free-text field as the creation wizard: the classification is
              the customer's taxonomy, and an archive must be re-classifiable
              into one that did not exist when it was created. */}
          <Autocomplete
            label="Classification"
            description={
              CLUSTER_LABEL[form.cluster] ?? "Yours to define — pick one in use, or type a new one"
            }
            value={form.cluster}
            disabled={!canEdit}
            maxLength={64}
            data={clusterOptions}
            onChange={(value) => setForm((v) => ({ ...v, cluster: value }))}
          />
        </Stack>
      </Paper>

      <Paper withBorder p="md" radius="md">
        <Text fw={600} mb="sm">
          Retention and storage
        </Text>
        <Stack gap="sm">
          <Group grow>
            <Select
              label="Retention class"
              value={form.retention_class}
              disabled={!canEdit}
              data={["5y", "10y", "15y", "30y", "permanent"]}
              onChange={(value) => setForm((v) => ({ ...v, retention_class: value ?? "10y" }))}
            />
            <Select
              label="Object Lock mode"
              value={form.object_lock_mode || "none"}
              disabled={!canEdit}
              data={[
                { value: "none", label: "Not applied" },
                { value: "GOVERNANCE", label: "Governance" },
                { value: "COMPLIANCE", label: "Compliance" },
              ]}
              onChange={(value) =>
                setForm((v) => ({ ...v, object_lock_mode: value === "none" ? "" : (value ?? "") }))
              }
            />
          </Group>
          <Group grow>
            <TextInput
              label="Storage prefix"
              value={form.storage_prefix}
              disabled={!canEdit}
              onChange={(e) => setForm((v) => ({ ...v, storage_prefix: e.currentTarget.value }))}
            />
            <Select
              label="Storage class"
              value={form.storage_class}
              disabled={!canEdit}
              data={["STANDARD", "GLACIER_IR"]}
              onChange={(value) => setForm((v) => ({ ...v, storage_class: value ?? "STANDARD" }))}
            />
          </Group>
          <Switch
            label="Index document text for full-text search"
            description="Extracted text is stored so it can be searched. Leave off for any corpus carrying personal data — it is not encrypted at rest in this build."
            checked={form.content_indexing_enabled}
            disabled={!canEdit}
            onChange={(e) =>
              setForm((v) => ({ ...v, content_indexing_enabled: e.currentTarget.checked }))
            }
          />
        </Stack>
      </Paper>

      {canEdit && (
        <Group>
          <Button disabled={!dirty} loading={busy} onClick={save}>
            Save changes
          </Button>
          {dirty && (
            <Text size="xs" c="dimmed">
              Unsaved changes.
            </Text>
          )}
        </Group>
      )}
    </Stack>
  );
}
